import { useCallback } from "react";

import { useCancelGenerationJob, useRetryGenerationJob } from "~/entities/chat";
import { useActiveWorkspace } from "~/entities/workspace";
import { useActiveProjectId } from "~/shared/store/useActiveProjectStore";

export interface ChatJobActions {
    cancel: (jobId: string) => Promise<void>;
    retry: (jobId: string) => Promise<void>;
    isCancelling: boolean;
    isRetrying: boolean;
}

/**
 * Объединяет cancel/retry мутации с актуальным workspace/project/scene контекстом.
 * `chatUuid` используется как fallback для project/scene, потому что в новом UI
 * chat не привязан к scene напрямую (см. useStartGeneration:46-49).
 */
export function useChatJobActions(chatUuid: string): ChatJobActions {
    const cancelMutation = useCancelGenerationJob();
    const retryMutation = useRetryGenerationJob();
    const { activeWorkspaceId } = useActiveWorkspace();
    const projectId = useActiveProjectId(activeWorkspaceId ?? undefined);

    const buildCtx = useCallback(
        () => ({
            workspaceId: activeWorkspaceId ?? undefined,
            projectId: projectId ?? chatUuid,
            sceneId: chatUuid,
        }),
        [activeWorkspaceId, projectId, chatUuid],
    );

    const cancel = useCallback(
        async (jobId: string) => {
            await cancelMutation.mutateAsync({ generationJobId: jobId, ...buildCtx() });
        },
        [cancelMutation, buildCtx],
    );

    const retry = useCallback(
        async (jobId: string) => {
            await retryMutation.mutateAsync({ generationJobId: jobId, ...buildCtx() });
        },
        [retryMutation, buildCtx],
    );

    return {
        cancel,
        retry,
        isCancelling: cancelMutation.isPending,
        isRetrying: retryMutation.isPending,
    };
}
